import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '@/lib/apiClient';
import { Helmet } from 'react-helmet-async';
import { Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import { trackSignUp } from '@/utils/customerAnalytics';

const VerifyEmailPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    // トークンがない場合は少し待ってからエラー判定（初回レンダリング時のタイミング対策）
    if (!token) {
      const timer = setTimeout(() => {
        setStatus('error');
        setMessage('無効なトークンです');
      }, 500);
      return () => clearTimeout(timer);
    }

    const verifyEmail = async () => {
      try {
        const response = await apiClient.patch(`/customer/email_verifications/${token}`);
        setStatus('success');
        setMessage(response.data.message);

        // トークンが返ってきた場合は自動ログイン
        if (response.data.token && response.data.user_type === 'customer') {
          setIsRedirecting(true);
          localStorage.removeItem('pendingVerificationEmail');

          // 広告コンバージョン: 顧客登録完了
          if (token) trackSignUp(token);

          try {
            await login({ token: response.data.token });
          } catch {
            // refreshUser 失敗してもトークンは保存済みなので続行
          }

          // ログイン完了後、即座にホームへ遷移
          window.location.href = '/';
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(error.response?.data?.error || 'エラーが発生しました');
      }
    };

    verifyEmail();
  }, [token, login]);

  return (
    <>
      <Helmet>
        <title>メール確認</title>
        <meta property="og:title" content="メール確認" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="pt-20">
          <div className="max-w-md mx-auto px-4 py-10">
            <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-400 border-theme rounded-theme-lg flex items-center justify-center shadow-theme-sm">
                  <Mail className="w-6 h-6 text-black" />
                </div>
              </div>
              <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-center text-black mb-6">メールアドレス確認</h1>

              {status === 'loading' && (
                <div className="text-center py-8">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto text-theme-primary mb-4" />
                  <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">処理中...</p>
                </div>
              )}

              {status === 'success' && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                    <CheckCircle className="w-8 h-8 text-black" />
                  </div>
                  <p className="[font-weight:var(--theme-font-weight-button)] text-green-700 mb-6">{message}</p>
                  {isRedirecting ? (
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="[font-weight:var(--theme-font-weight-button)]">ログイン中...</span>
                    </div>
                  ) : (
                    <a
                      href="/login"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                    >
                      ログインページへ
                    </a>
                  )}
                </div>
              )}

              {status === 'error' && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                    <XCircle className="w-8 h-8 text-black" />
                  </div>
                  <p className="[font-weight:var(--theme-font-weight-button)] text-red-700 mb-6">{message}</p>
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-black shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    ホームに戻る
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmailPage;
